import React from 'react';

import Button from '../Button';
import MultilineText from '../MultilineText';
import useCheckoutState from '../../hooks/useCheckoutState';
import { getCheckoutConfirmation } from './helpers';
import getButtonBackgroundColor from '../../lib/getButtonBackgroundColor';
import getButtonTextColor from '../../lib/getButtonTextColor';
import FilesList from '../FilesList/FilesList';
import request from '../../services/request';
import { apiUrl } from '../../services/env';
import downloadUrlAsFile from '../../services/downloadUrlAsFile';
import getSellerPortalUrl from '../../lib/getSellerPortalUrl';

const ChargeStatusMessage = ({
  charge = {},
  status,
  email,
  seller = {},
  checkout = {},
  customer = {},
  preview,
  isConfirmation,
}) => {
  const { restartPayment } = useCheckoutState();
  const isDisabled = preview;

  switch (status) {
    case 'pending':
    case 'partially_paid':
    case 'unpaid':
    case 'refunded':
    case 'partial_refund':
    case 'paid': {
      const { title, message, type } = getCheckoutConfirmation({
        checkout,
        charge,
        seller,
        email,
      });

      const hasFiles = charge.files && charge.files.length > 0;

      return (
        <>
          <div className="cp_status-wrapper text-center p-4 w-full">
            <h2 className="cp_status-title text-black mb-2">
              <div
                className="flex justify-center mb-4 cp_status-icon"
                style={{
                  color: seller.brandColor,
                }}
              >
                <svg width="56" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="m19 28 6 6 12-12m18 6a27 27 0 1 1-54 0 27 27 0 0 1 54 0Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {title}
            </h2>
            <div className="cp_status-content mb-4">
              {type === 'html' ? (
                <MultilineText
                  className="cp_status-text"
                  dangerouslySetInnerHTML={{
                    __html: message,
                  }}
                />
              ) : (
                <MultilineText className="cp_status-text">{message}</MultilineText>
              )}
            </div>

            {hasFiles && checkout.showFilesList && (
              <div className="mb-8">
                <FilesList.List>
                  {charge.files?.map((file, index) => (
                    <FilesList.Item
                      key={file._id}
                      file={file}
                      isFirst={index === 0}
                      isPreview={preview}
                      onDownloadFile={async () => {
                        const response = await request(
                          apiUrl +
                            `/customers/${customer._id}/files/${file._id}/download?token=${customer.loginToken}`,
                        );

                        if (response?.data?.url) {
                          downloadUrlAsFile(response?.data?.url, file.name);
                        } else {
                          window.alert(
                            'Unable to download file. Please try again or contact support.',
                          );
                        }
                      }}
                    />
                  ))}
                </FilesList.List>
              </div>
            )}

            {hasFiles && checkout.showViewDownloadsButton && (
              <div className="mb-4">
                <Button
                  appearance="black"
                  to={
                    isDisabled
                      ? null
                      : `${getSellerPortalUrl(seller)}/${customer.loginToken}/payments/${charge._id}`
                  }
                  ignoreBasename
                  className="cp_button cp_view-downloads-button"
                  size="full"
                  target="_blank"
                  disabled={isDisabled}
                  backgroundColor={getButtonBackgroundColor({
                    buttonBackgroundColor: checkout.buttonBackgroundColor,
                    brandColor: seller.brandColor,
                  })}
                  color={getButtonTextColor({
                    buttonTextColor: checkout.buttonTextColor,
                    buttonBackgroundColor: checkout.buttonBackgroundColor,
                    brandColor: seller.brandColor,
                  })}
                >
                  {checkout.viewDownloadsText}
                </Button>
              </div>
            )}
          </div>
        </>
      );
    }

    case 'failed':
    case 'canceled':
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Payment failed</h2>
          <div className="cp_status-content mb-4">
            Your payment failed and has not been processed.
            <br />
            Please try again.
          </div>
          <Button
            handleClick={() => {
              restartPayment({ isConfirmation });
            }}
            appearance="black"
            className="cp_button"
            size="full"
            disabled={isDisabled}
            backgroundColor={getButtonBackgroundColor({
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
            color={getButtonTextColor({
              buttonTextColor: checkout.buttonTextColor,
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
          >
            Try again
          </Button>
        </div>
      );
    default:
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Payment pending</h2>
          <div className="cp_status-content mb-4">
            Your payment has been received and is awaiting payment confirmation. Please contact{' '}
            {seller.displayName || 'the seller'} to confirm your payment.
          </div>
        </div>
      );
  }
};

export default ChargeStatusMessage;
